import React, { useCallback } from 'react';
import styled from 'styled-components';
import { useTranslate } from '@bynder/localization';
import { token } from '@bynder/design-system';
import { IconTextField, IconTitle } from '@bynder/icons';
import { CustomChip } from './components/CustomChip';
import { SearchOptionType } from '../helpers/types';
import { CHIP_TOGGLE_OPTIONS, SEARCH_OPTION_DEFINITIONS } from './constants';
import { useSearchScope } from '../SpotlightState';

export function SearchOptions() {
	const { selectedSearchOptions, setSelectedSearchOptions } = useSearchScope();
	const { translate } = useTranslate();

	const toggleSearchOption = useCallback(
		(chipKey: SearchOptionType) => {
			setSelectedSearchOptions((prev) => {
				if (prev.some((option) => option.key === chipKey)) {
					return prev.filter((option) => option.key !== chipKey);
				}

				return [...prev, SEARCH_OPTION_DEFINITIONS[chipKey].parseFromQuery('')];
			});
		},
		[setSelectedSearchOptions],
	);

	// We do this to prevent the search input from losing focus when selecting/deselecting search options
	const preventFocusLoss = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
	};

	return (
		<StyledDiv>
			<div>{translate('spotlight.searchOptions.search-in')}</div>
			<StyledChipsContainer>
				{CHIP_TOGGLE_OPTIONS.map(({ key, translationKey }) => (
					<CustomChip
						isSelected={selectedSearchOptions.some((option) => option.key === key)}
						icon={key === SearchOptionType.Title ? <IconTitle /> : <IconTextField />}
						onSelect={() => toggleSearchOption(key)}
						onMouseDown={preventFocusLoss}
						key={key}
					>
						{translate(translationKey)}
					</CustomChip>
				))}
			</StyledChipsContainer>
		</StyledDiv>
	);
}

const StyledDiv = styled.div`
	padding: 0 ${token.spacing5} ${token.spacing3};
	margin: ${token.spacing3} 0 0;
	border-bottom: 1px solid #0310261a;
`;

const StyledChipsContainer = styled.div`
	display: flex;
	gap: ${token.spacing2};
	margin-top: ${token.spacing3};
`;
